import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { lastValueFrom, Observable } from 'rxjs';

import {
  AnswerViewModel,
  CreateQuestionCommand,
  QuestionnaireViewModel,
  SaveAnswersCommand,
  UpdateQuestionCommand,
  UpdateAnswersCommand,
} from '../models/questionnaire-api.model';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class QuestionnaireApiService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = environment.apiBaseUrl;

  getQuestionnaire(classNumber: string): Observable<QuestionnaireViewModel> {
    return this.http.get<QuestionnaireViewModel>(`${this.apiBaseUrl}/api/Questionnaire/${encodeURIComponent(classNumber)}`);
  }

  createQuestion(command: CreateQuestionCommand): Observable<number> {
    return this.http.post<number>(`${this.apiBaseUrl}/api/Questionnaire/questions`, command);
  }

  updateQuestion(command: UpdateQuestionCommand): Observable<void> {
    return this.http.put<void>(`${this.apiBaseUrl}/api/Questionnaire/questions/${command.id}`, command);
  }

  deleteQuestion(questionId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiBaseUrl}/api/Questionnaire/questions/${questionId}`);
  }

  getAnswersByProject(projectId: number): Observable<AnswerViewModel[]> {
    return this.http.get<AnswerViewModel[]>(`${this.apiBaseUrl}/api/Questionnaire/answers/project/${projectId}`);
  }

  exportAnswersByProject(projectId: number): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.apiBaseUrl}/api/Questionnaire/answers/project/${projectId}/export`, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  saveAnswers(command: SaveAnswersCommand): Observable<void> {
    return this.http.post<void>(`${this.apiBaseUrl}/api/Questionnaire/answers`, command);
  }

  updateAnswers(command: UpdateAnswersCommand): Observable<void> {
    return this.http.put<void>(`${this.apiBaseUrl}/api/Questionnaire/answers`, command);
  }

  normalizeAttachmentPath(value: string | null | undefined): string {
    const trimmed = (value ?? '').trim();
    if (!trimmed) {
      return '';
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && typeof parsed.blobName === 'string') {
        return parsed.blobName;
      }
    } catch (error) {
      console.error(error);
      // Stored value is already a plain blob name.
    }

    return trimmed.replace(/^"|"$/g, '');
  }

  async uploadAttachment(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    try {
      const response = await lastValueFrom(
        this.http.post(`${this.apiBaseUrl}/api/Files/upload`, formData, {
          withCredentials: true,
          observe: 'response',
          responseType: 'text',
        }),
      );

      return this.normalizeAttachmentPath(response.body ?? '');
    } catch (error: any) {
      console.error('File upload failed:', error?.status, error?.statusText);
      throw error;
    }
  }

  async downloadAttachment(blobName: string): Promise<Blob> {
    const normalizedBlobName = this.normalizeAttachmentPath(blobName);

    try {
      const response = await lastValueFrom(
        this.http.get(`${this.apiBaseUrl}/api/Files/download/${encodeURIComponent(normalizedBlobName)}`, {
          withCredentials: true,
          observe: 'response',
          responseType: 'blob',
        }),
      );

      return response.body ?? new Blob();
    } catch (error: any) {
      console.error('File download failed:', error?.status, error?.statusText);
      throw error;
    }
  }

  async deleteAttachment(blobName: string): Promise<void> {
    const normalizedBlobName = this.normalizeAttachmentPath(blobName);
    if (!normalizedBlobName) {
      return;
    }

    try {
      await lastValueFrom(
        this.http.delete<void>(`${this.apiBaseUrl}/api/Files/${encodeURIComponent(normalizedBlobName)}`, {
          withCredentials: true,
        }),
      );
    } catch (error: any) {
      console.error('File delete failed:', error?.status, error?.statusText);
      throw error;
    }
  }
}

